import { axiosInstance } from "../utils/ApiClient";

/**
 * Alert feedback service — confirm/dismiss endpoints.
 *
 * Both endpoints are idempotent: re-firing the same state still succeeds
 * and updates feedback_at (so undo→redo events are visible in PostHog digest).
 *
 * Backend: POST /alerts/<id>/confirm/ and POST /alerts/<id>/dismiss/
 * Both return the full updated alert object for re-render without refetch.
 */
export const alertFeedbackService = {
  /**
   * Confirm an alert as a true positive.
   * @param {string} alertId - UUID of the alert
   * @returns {Promise<Object>} Updated alert from backend
   */
  confirmAlert: async (alertId) => {
    const response = await axiosInstance.post(
      `/alerts/${alertId}/confirm/`,
      {},
    );
    return response.data;
  },

  /**
   * Dismiss an alert with a reason.
   * @param {string} alertId - UUID of the alert
   * @param {string} reason - One of: not_relevant | wrong_account | false_alarm | already_addressed | other
   * @param {string} [note] - Optional free-form note (only when reason === 'other')
   * @returns {Promise<Object>} Updated alert from backend
   */
  dismissAlert: async (alertId, reason, note) => {
    const body = { reason };
    if (note !== undefined && note !== null && note !== "") {
      body.note = note;
    }
    const response = await axiosInstance.post(
      `/alerts/${alertId}/dismiss/`,
      body,
    );
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Resolve / snooze / unsnooze live on the *actions* ViewSet, NOT /alerts/.
  // (confirm/dismiss were built as standalone views; the rest as a ViewSet.)
  // The {id} is the same AccountAlert UUID across both families.
  // ---------------------------------------------------------------------------

  /**
   * Resolve a signal — the underlying issue has been handled.
   * Moves the signal to the "Resolved" tab.
   * @param {string} id - AccountAlert UUID
   * @param {string} [note] - Optional resolution note
   * @returns {Promise<Object>} Updated alert from backend
   */
  resolveAlert: async (id, note) => {
    const body = {};
    if (note !== undefined && note !== null && note !== "") {
      body.note = note;
    }
    const response = await axiosInstance.post(`/actions/${id}/resolve/`, body);
    return response.data;
  },

  /**
   * Snooze a signal until a future date — defers it without resolving.
   * Moves the signal to the "Snoozed" tab until `snoozeUntil` passes.
   * @param {string} id - AccountAlert UUID
   * @param {string} snoozeUntil - ISO datetime to snooze until
   * @returns {Promise<Object>} Updated alert from backend
   */
  snoozeAlert: async (id, snoozeUntil) => {
    const response = await axiosInstance.post(`/actions/${id}/snooze/`, {
      snooze_until: snoozeUntil,
    });
    return response.data;
  },

  /**
   * Unsnooze a snoozed signal — returns it to "Active".
   * Only valid for snoozed signals.
   * @param {string} id - AccountAlert UUID
   * @returns {Promise<Object>} Updated alert from backend
   */
  unsnoozeAlert: async (id) => {
    const response = await axiosInstance.post(`/actions/${id}/unsnooze/`, {});
    return response.data;
  },
};
